import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import toast from 'react-hot-toast'
import Layout from '../../components/Layout'
import Spinner from '../../components/Spinner'
import { getClientsRegistry, getOrdersRegistry, getPaymentsRegistry, exportClients, exportOrders, exportPayments } from '../../api/registry'
import { formatMoney, formatDate } from '../../utils/format'
import { useAuth } from '../../context/AuthContext'
import { useT } from '../../i18n'

type Tab = 'clients' | 'orders' | 'payments'

export default function RegistryPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const lang = user?.language ?? 'ru'
  const tr = useT(lang)
  const [tab, setTab] = useState<Tab>('clients')
  const [exporting, setExporting] = useState(false)

  const clientsQ = useQuery({
    queryKey: ['registry-clients'],
    queryFn: () => getClientsRegistry({ limit: 100 }),
    enabled: tab === 'clients',
  })

  const ordersQ = useQuery({
    queryKey: ['registry-orders'],
    queryFn: () => getOrdersRegistry({ limit: 100 }),
    enabled: tab === 'orders',
  })

  const paymentsQ = useQuery({
    queryKey: ['registry-payments'],
    queryFn: () => getPaymentsRegistry({ limit: 100 }),
    enabled: tab === 'payments',
  })

  const TABS: { key: Tab; label: string }[] = [
    { key: 'clients',  label: tr('clients') },
    { key: 'orders',   label: tr('orders') },
    { key: 'payments', label: tr('payments') },
  ]

  const isLoading = tab === 'clients' ? clientsQ.isLoading : tab === 'orders' ? ordersQ.isLoading : paymentsQ.isLoading

  async function handleExport() {
    setExporting(true)
    try {
      if (tab === 'clients') await exportClients()
      else if (tab === 'orders') await exportOrders()
      else await exportPayments()
    } catch {
      toast.error(tr('error'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <Layout title={tr('registry')} hideNav>
      <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#A8A8AE', marginBottom: 16, fontSize: 13, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        Назад
      </button>
      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              flex: 1, padding: '9px', borderRadius: 12,
              fontFamily: '"Instrument Sans", sans-serif', fontSize: 12, fontWeight: 500,
              background: tab === t.key ? '#F5F4F0' : 'transparent',
              color: tab === t.key ? '#0A0A0B' : '#A8A8AE',
              border: tab === t.key ? 'none' : '1px solid rgba(255,255,255,0.10)',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Export button */}
      <button
        onClick={handleExport}
        disabled={exporting}
        className="flex items-center justify-center gap-2 py-2.5 rounded-xl mb-4 text-sm font-semibold w-full disabled:opacity-40"
        style={{ background: 'rgba(111,184,154,0.10)', color: '#6FB89A' }}
      >
        <Download size={16} /> {exporting ? tr('loading') : `${tr('export')} Excel`}
      </button>

      {isLoading ? (
        <Spinner className="mt-6" />
      ) : tab === 'clients' ? (
        <ClientsTable data={(clientsQ.data?.clients as any[]) ?? []} tr={tr} />
      ) : tab === 'orders' ? (
        <OrdersTable data={(ordersQ.data?.orders as any[]) ?? []} tr={tr} />
      ) : (
        <PaymentsTable data={(paymentsQ.data?.payments as any[]) ?? []} tr={tr} />
      )}
    </Layout>
  )
}

type Tr = ReturnType<typeof useT>

function ClientsTable({ data, tr }: { data: any[]; tr: Tr }) {
  if (!data.length) return <p className="text-center text-tg-hint">{tr('noData')}</p>
  return (
    <div className="flex flex-col gap-2">
      {data.map((c: any) => (
        <div key={c.id} className="card text-sm">
          <p className="font-medium text-tg-text">{c.full_name}</p>
          <p className="text-tg-hint text-xs">{c.phone}</p>
          <div className="flex gap-4 mt-1 text-xs text-tg-hint">
            <span>{tr('ordersCount')} {c.total_orders}</span>
            <span>{tr('activeCount')} {c.active_orders}</span>
            {c.total_debt > 0 && <span className="text-tg-danger">{tr('debtLabel')} {formatMoney(c.total_debt)}</span>}
          </div>
        </div>
      ))}
    </div>
  )
}

function OrdersTable({ data, tr }: { data: any[]; tr: Tr }) {
  if (!data.length) return <p className="text-center text-tg-hint">{tr('noData')}</p>
  return (
    <div className="flex flex-col gap-2">
      {data.map((o: any) => (
        <div key={o.id} className="card text-sm">
          <div className="flex justify-between">
            <span className="font-medium text-tg-text">{o.registry_num}</span>
            <span className="text-xs text-tg-hint">{o.status}</span>
          </div>
          <p className="text-tg-hint text-xs">{o.client_name} · {o.product_name}</p>
          <div className="flex gap-3 mt-1 text-xs text-tg-hint">
            <span>{o.months} {tr('monthsShort')}</span>
            <span>{formatMoney(o.monthly_amount)}{tr('perMonthShort')}</span>
            <span>{tr('remainingLabel')} {formatMoney(o.remaining_amount)}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

function PaymentsTable({ data, tr }: { data: any[]; tr: Tr }) {
  if (!data.length) return <p className="text-center text-tg-hint">{tr('noData')}</p>
  return (
    <div className="flex flex-col gap-2">
      {data.map((p: any) => (
        <div key={p.id} className={`card text-sm ${p.status === 'overdue' ? 'border border-tg-danger/20' : ''}`}>
          <div className="flex justify-between">
            <span className="font-medium text-tg-text">{p.registry_num} #{p.payment_num}</span>
            <span className={`text-xs ${p.status === 'overdue' ? 'text-tg-danger' : 'text-tg-hint'}`}>{p.status}</span>
          </div>
          <p className="text-tg-hint text-xs">{p.client_name}</p>
          <div className="flex gap-3 mt-1 text-xs text-tg-hint">
            <span>{formatMoney(p.amount)}</span>
            <span>{tr('until')}: {formatDate(p.due_date)}</span>
            {p.days_overdue > 0 && <span className="text-tg-danger">{p.days_overdue} {tr('overdueDays')}</span>}
          </div>
        </div>
      ))}
    </div>
  )
}
